import { useAuth } from '@/app/_auth/auth-hooks'
import {
  DOMAIN_ID_QUERY_KEY,
  useDomainIdQueryState,
} from '@/app/_common/domain/use-domain-from-url'
import {
  SPLIT_VIEW_QUERY_KEY,
  useSplitViewQueryState,
} from '@/app/_common/split-view/split-view-context'
import emailjs from '@emailjs/browser'
import { useMutation, UseMutationOptions } from '@tanstack/react-query'

type EmailMessageOutputMutationVariables = {
  workflowId: string
  fileUrl?: string | null
  title?: string
}

export function useEmailMessageOutputMutation(
  options: UseMutationOptions<
    { recipientEmail: string },
    Error,
    EmailMessageOutputMutationVariables
  > = {},
) {
  const { user } = useAuth()
  const { domainId } = useDomainIdQueryState()
  const viewState = useSplitViewQueryState()
  return useMutation({
    mutationKey: ['send-workflow-email'],
    mutationFn: async ({ workflowId, fileUrl, title }) => {
      if (!user?.email) throw new Error('SEND_WORKFLOW_EMAIL_USER_EMAIL_NOT_FOUND')

      const emailResponse = await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_WORKFLOW_TEMPLATE_ID,
        {
          user_email: user.email,
          prompt: title,
          to_name: user.fullName,
          workflow_id: workflowId,
          workflow_url: `${window.location.origin}?${DOMAIN_ID_QUERY_KEY}=${domainId}&${SPLIT_VIEW_QUERY_KEY}=${viewState}`,
          filename: `${workflowId}.pdf`,
          document: fileUrl,
        },
        { publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY },
      )

      if (emailResponse.status !== 200) throw new Error('WORKFLOW_EMAIL_SEND_FAILED')

      return {
        recipientEmail: user.email,
      }
    },
    ...options,
  })
}
